import React, { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { checkFileRegeneration } from "@/modules/patients/detail/progress-note/documents/check-file-regeneration"
import { ProgressNoteDocument } from "@/modules/patients/detail/progress-note/types/document"
import {
  getChProgressNotesByAppointmentId,
  getDeProgressNotesByAppointmentId,
  getPreviewProgressNotesByAppointmentId,
} from "@/modules/patients/detail/store/useDocumentStore"
import { chSaveCarePlanDataMutation$data } from "@/modules/patients/services/ch/__generated__/chSaveCarePlanDataMutation.graphql"
import { deSaveCarePlanDataMutation$data } from "@/modules/patients/services/de/__generated__/deSaveCarePlanDataMutation.graphql"
import { Country } from "@/types/__generated__/union-types"

type State = {
  selectedDocument: ProgressNoteDocument | null
  triggeredValidation: boolean
  markdownPreviews: (chSaveCarePlanDataMutation$data | deSaveCarePlanDataMutation$data)[]
  isConcluded: boolean
}

type Actions = {
  actions: {
    setSelectedDocument: (selectedDocument: ProgressNoteDocument | null) => void
    setTriggeredValidation: () => void
    findNextDocument: (
      currentId: string,
      countryCode: Country,
      appointmentId: string | null,
    ) => ProgressNoteDocument | null
    setMarkdownPreviews: (
      markdownPreviews: (chSaveCarePlanDataMutation$data | deSaveCarePlanDataMutation$data)[],
    ) => void
    setIsConcluded: () => void
  }
}

const initialState: State = {
  selectedDocument: null,
  triggeredValidation: false,
  markdownPreviews: [],
  isConcluded: false,
}

const createConcludeDialogStore = () => {
  return create<State & Actions>((set) => {
    return {
      ...initialState,

      actions: {
        setSelectedDocument: (selectedDocument) => {
          set({ selectedDocument })
        },
        setTriggeredValidation: () => {
          set({
            triggeredValidation: true,
          })
        },
        findNextDocument: (currentId, countryCode, appointmentId) => {
          const progressNotes =
            countryCode === "CH"
              ? getChProgressNotesByAppointmentId(appointmentId)
              : getDeProgressNotesByAppointmentId(appointmentId)

          if (!progressNotes) return null

          const rawProgressNotes = getPreviewProgressNotesByAppointmentId(appointmentId)
          for (const [progressNoteId, entry] of progressNotes) {
            const rawProgressNote = rawProgressNotes.find(
              (p) => p.episodeOfCareProgressNoteData.id === progressNoteId,
            )?.episodeOfCareProgressNoteData

            const followUpAppointment =
              rawProgressNote?.__typename === "ch_ProgressNote" ? rawProgressNote.followUpAppointment : undefined

            const document = Object.values(entry).find((value) => {
              const selectedDocument: ProgressNoteDocument = value
              return (
                selectedDocument &&
                selectedDocument.id !== currentId &&
                (!selectedDocument.signedAt ||
                  checkFileRegeneration(
                    selectedDocument,
                    followUpAppointment?.fileUploadRequest,
                    followUpAppointment?.medgateConnectOrder,
                  ).needsRegeneration)
              )
            })

            if (document) {
              return {
                ...document,
                fileUploadRequest: followUpAppointment?.fileUploadRequest,
                medgateConnectOrder: followUpAppointment?.medgateConnectOrder,
              }
            }
          }

          return null
        },
        setMarkdownPreviews: (
          markdownPreviews: (chSaveCarePlanDataMutation$data | deSaveCarePlanDataMutation$data)[],
        ) => {
          set({ markdownPreviews })
        },
        setIsConcluded: () => {
          set({
            isConcluded: true,
          })
        },
      },
    }
  })
}

type ConcludeDialogStoreType = ReturnType<typeof createConcludeDialogStore>

const ConcludeDialogStoreContext = createContext<ConcludeDialogStoreType | null>(null)

type ConcludeDialogStoreProviderProps = React.PropsWithChildren

export const ConcludeDialogStoreProvider = ({ children }: ConcludeDialogStoreProviderProps) => {
  const storeRef = useRef<ConcludeDialogStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createConcludeDialogStore()
  }
  return <ConcludeDialogStoreContext.Provider value={storeRef.current}>{children}</ConcludeDialogStoreContext.Provider>
}

export function useConcludeDialogStoreContext<T>(selector: (state: State & Actions) => T): T {
  const store = useContext(ConcludeDialogStoreContext)
  if (!store) throw new Error("Missing ConcludeDialogStoreProvider in the tree")
  return useStore(store, selector)
}
